import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { verifyPasscode, requestPin, verifyPin } from '../api/participantAuth';
import { useParticipants } from '../hooks/useParticipants';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const ParticipantLoginPage = () => {
  const { getParticipantById, loadParticipants } = useParticipants();
  const history = useHistory();
  const location = useLocation();
  const [step, setStep] = useState('method'); // 'method', 'pincode'
  const [passcode, setPasscode] = useState('');
  const [email, setEmail] = useState('');
  const [pincode, setPincode] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [participant, setParticipant] = useState(null);
  const [verificationId, setVerificationId] = useState(''); // Store verificationId from requestPin
  const [authToken, setAuthToken] = useState(''); // Store auth token from verifyPin
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [message, setMessage] = useState('');

  // Ensure participants are loaded when page mounts
  useEffect(() => {
    const initializeData = async () => {
      try {
  const data = await loadParticipants();
      } catch (err) {
        console.error('Failed to load participants:', err);
      }
    };
    initializeData();
  }, []);

  useEffect(() => {
    // Get participantId from URL params or location state
    const params = new URLSearchParams(location.search);
    const participantIdParam = params.get('participantId') || location.state?.participantId || '';
    if (participantIdParam) {
      // Load participant data and check if device is remembered
      loadParticipantData(participantIdParam);
    }
  }, [location]);

  const loadParticipantData = async (participantId) => {
    // Load participant data first
    try {
      setLoading(true);
      
      // Try to get participant by ID directly from store
      const participantFromStore = await getParticipantById(participantId);
      
      if (participantFromStore) {
        setParticipant(participantFromStore);
        setEmail(participantFromStore.emailAddress || participantFromStore.mobile || '');
        setLoading(false);
        
        // Check for auto-login
        const deviceId = getDeviceFingerprint();
        const identifier = participantFromStore.emailAddress || participantFromStore.mobile;
        const storedAuth = localStorage.getItem(`auth_${identifier}_${deviceId}`);
        
        if (storedAuth) {
          try {
            const authData = JSON.parse(storedAuth);
            setLoading(true);
            const loginResult = await verifyPasscode(identifier, authData.passcode);
            
            if (loginResult.success) {
              completeLogin(loginResult.participant);
              return;
            } else {
              localStorage.removeItem(`auth_${identifier}_${deviceId}`);
            }
          } catch (err) {
            console.error('Auto-login failed:', err);
          } finally {
            setLoading(false);
          }
        }
        return;
      }
      
      // Fallback: try verifyPasscode (old method)
      const result = await verifyPasscode(null, null, true, participantId);
      if (result.success) {
        const participantData = result.participant;
        setParticipant(participantData);
        setEmail(participantData.emailAddress || participantData.mobile || '');
        
        // Check if device is remembered for auto-login
        const deviceId = getDeviceFingerprint();
        const identifier = participantData.emailAddress || participantData.mobile;
        const storedAuth = localStorage.getItem(`auth_${identifier}_${deviceId}`);
        
        if (storedAuth) {
          try {
            const authData = JSON.parse(storedAuth);
            setLoading(true);
            const loginResult = await verifyPasscode(identifier, authData.passcode);
            
            if (loginResult.success) {
              completeLogin(loginResult.participant);
              return;
            } else {
              localStorage.removeItem(`auth_${identifier}_${deviceId}`);
            }
          } catch (err) {
            console.error('Auto-login failed:', err);
          } finally {
            setLoading(false);
          }
        }
      } else {
        setError('Participant not found. Please return to the participants list.');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to load participant data.');
      console.error(err);
      setLoading(false);
    }
  };

  const handleMethodSelect = async (method) => {
    setSelectedMethod(method);
    setError('');
    setSuccessMessage('');
    setMessage('');
    setLoading(true);
    
    try {
      if (!participant) {
        setError('Participant information not loaded. Please try again.');
        setLoading(false);
        return;
      }

      const deviceId = getDeviceFingerprint();
      const accountId = "7"; // Account ID for CJC
      
      
      // Call new SignOn API endpoint
      const result = await requestPin(participant.id, method, deviceId, accountId);
      
      
      if (result.success && result.verificationId) {
        // Store the verificationId for PIN verification
        setVerificationId(result.verificationId);
        setStep('pincode');
      } else {
        setError(result.message || `Failed to send PIN code via ${method}. Please try again.`);
      }
    } catch (err) {
      setError('Failed to send PIN code. Please try again.');
      console.error('handleMethodSelect error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePincodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!pincode || pincode.length !== 4) {
      setError('Please enter the 4-digit PIN code');
      return;
    }

    setLoading(true);
    
    try {
      const deviceId = getDeviceFingerprint();
      const accountId = "7"; // Account ID for CJC
      
      
      // Call new VerifyPin API endpoint
      const result = await verifyPin(verificationId, pincode, deviceId, accountId);
      
      
      if (result.success && result.token) {
        // Store auth token from API response
        setAuthToken(result.token);
        
        // Store auth token and device info for persistent login
        if (rememberDevice) {
          const identifier = result.participant.email || result.participant.mobile;
          localStorage.setItem(`auth_${identifier}_${deviceId}`, JSON.stringify({
            token: result.token,
            deviceId: deviceId,
            expiresIn: result.expiresIn,
            savedAt: new Date().toISOString()
          }));
        }
        
        completeLogin(result.participant, result.token, result.expiresIn);
      } else {
        setError(result.message || 'Invalid PIN code. Please try again.');
        setPincode(''); // Clear PIN on error
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('handlePincodeSubmit error:', err);
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (participantData, token, expiresIn) => {
    const existingSession = sessionStorage.getItem('participantAuth');
    if (existingSession) {
      try {
        const parsed = JSON.parse(existingSession);
      } catch {}
    }
    
    // Calculate expiration time
    const expiresAt = new Date(Date.now() + (expiresIn * 1000)).toISOString();
    
    // Store participant data and auth token in sessionStorage
    // Always use participantData (API) for all fields to avoid stale state
    const authData = {
      email: participantData.emailAddress || participantData.email,
      participantId: participantData.id,
      fullName: participantData.fullName,
      role: Array.isArray(participantData.role) ? [...participantData.role] : (participantData.role ? [participantData.role] : []),
      section: participantData.handlerInfo?.section || participantData.section,
      loginTime: new Date().toISOString(),
      expiresAt: expiresAt,
      authToken: {
        Token: token // Store the auth token from VerifyPin API
      }
    };
    
    console.log('ParticipantLoginPage - Auth data being stored:', authData);
    sessionStorage.setItem('participantAuth', JSON.stringify(authData));
    
    // Navigate to dashboard with full participant data
    const fullParticipantData = participant ? { ...participant, ...participantData } : participantData;
    history.push('/race/dashboard', { participant: fullParticipantData });
  };

  const handleResendPincode = () => {
    handleMethodSelect(selectedMethod);
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-6 col-lg-5">
          <div className="card shadow-sm border-0">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <div 
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #0b3d91 0%, #1e5bb8 100%)' }}
                >
                  <i className="bi bi-lock-fill text-white" style={{ fontSize: '2rem' }}></i>
                </div>
                <h2 style={{ color: '#0b3d91' }}>Participant Login</h2>
                <p className="text-muted">
                  {step === 'method' && 'Choose how to receive your PIN code'}
                  {step === 'pincode' && 'Enter the PIN code sent to you'}
                </p>
              </div>

              {/* Step 1: Method Selection */}
              {step === 'method' && participant && (
                <div>
                  <div className="alert alert-info mb-4">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Welcome, {participant.fullName}!</strong>
                    <div className="small mt-2">
                      We will send you a 4-digit PIN code to verify your identity. 
                      Choose how you'd like to receive it:
                    </div>
                    <div className="small mt-2">
                      <b>Not you?</b> <a href="/race/participants" className="text-decoration-none">Back to participant list</a>
                    </div>
                  </div>

                  <div className="d-grid gap-3 mb-4">
                    {(() => {
                      // Get notification methods
                      const notificationMethods = participant.preferredNotificationMethod 
                        ? (Array.isArray(participant.preferredNotificationMethod) 
                            ? participant.preferredNotificationMethod 
                            : [participant.preferredNotificationMethod])
                        : [];
                      
                      // Filter out in-app notifications
                      const availableMethods = notificationMethods.filter(method => 
                        method.toLowerCase() !== 'in-app'
                      );
                      
                      // If no methods available, provide default options based on what user has
                      const methodsToShow = availableMethods.length > 0 
                        ? availableMethods 
                        : [
                            ...(participant.emailAddress ? ['email'] : []),
                            ...(participant.mobile ? ['text'] : [])
                          ];
                      
                      return methodsToShow.map((method) => (
                        <button
                          key={method}
                          type="button"
                          className="btn btn-outline-primary btn-lg text-start"
                          onClick={() => handleMethodSelect(method.toLowerCase())}
                          disabled={loading}
                          style={{ borderWidth: '2px' }}
                        >
                          <div className="d-flex align-items-center">
                            <i className={`bi ${method.toLowerCase() === 'email' ? 'bi-envelope-fill' : 'bi-phone-fill'} me-3`} style={{ fontSize: '1.5rem' }}></i>
                            <div>
                              <div className="fw-bold">Send via {method.toLowerCase() === 'email' ? 'Email' : 'Text/SMS'}</div>
                              <small className="text-muted">
                                {method.toLowerCase() === 'email' ? participant.emailAddress : participant.mobile}
                              </small>
                            </div>
                          </div>
                        </button>
                      ));
                    })()}
                  </div>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={() => history.push('/race/participants')}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Participants List
                  </button>
                </div>
              )}

              {/* Step 3: PIN Code Entry */}
              {step === 'pincode' && (
                <form onSubmit={handlePincodeSubmit}>
                  <div className="alert alert-info mb-4">
                    <i className="bi bi-shield-lock me-2"></i>
                    <div className="small">
                      A 4-digit PIN code has been sent to {selectedMethod === 'email' ? participant.emailAddress : participant.mobile}. 
                      {selectedMethod === 'email' && ' Please check your inbox (and spam folder).'}
                      {' '}It may take a few minutes to arrive. Enter the code below to complete the login process.
                    </div>
                  </div>

                  {message && (
                    <div className="alert alert-info mb-4" role="alert">
                      <i className="bi bi-info-circle me-2"></i>
                      {message}
                    </div>
                  )}

                  <div className="mb-4">
                    <label htmlFor="pincode" className="form-label fw-bold">4-Digit PIN Code</label>
                    <input
                      type="text"
                      className="form-control form-control-lg text-center"
                      id="pincode"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="••••"
                      required
                      autoFocus
                      maxLength="4"
                      style={{ fontSize: '2rem', letterSpacing: '1rem' }}
                    />
                    <div className="text-end mt-2">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={handleResendPincode}
                        disabled={loading}
                      >
                        Didn't receive it? Resend PIN
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="rememberDevice"
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="rememberDevice">
                        <strong>Remember this device</strong>
                        <div className="alert alert-warning mt-2 mb-0 py-2 px-3 small">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          <strong>Warning:</strong> Anyone who uses this device will have access to your account. Only enable this on your personal device.
                        </div>
                      </label>
                    </div>
                  </div>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100 mb-3"
                    disabled={loading}
                    style={{ background: 'linear-gradient(135deg, #0b3d91 0%, #1e5bb8 100%)', border: 'none' }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-box-arrow-in-right me-2"></i>
                        Login
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={() => {
                      setStep('method');
                      setPincode('');
                      setError('');
                      setSuccessMessage('');
                    }}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back
                  </button>
                </form>
              )}

              <hr className="my-4" />

              <div className="text-center">
                <small className="text-muted">
                  Don't have an account?{' '}
                  <a href="/race/signup" className="text-decoration-none">
                    Sign up here
                  </a>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantLoginPage;
